import { Editor, useEditorState } from '@tiptap/react';
import { FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isUrl } from '../../../../utils';
import { Button, ButtonType } from '../../../Button';
import { IconLabel } from '../../../IconLabel';
import { InputField } from '../../../InputField';
import { Modal } from '../../../Modal';

import classNames from 'classnames';
import classes from '../InlineTools.module.scss';
import linkClasses from './Link.module.scss';

interface LinkProps {
  editor: Editor;
}

export const Link: FunctionComponent<LinkProps> = ({ editor }) => {
  const { t } = useTranslation();

  const { isActive, isActiveCode } = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isActive: editor.isActive('link'),
      isActiveCode: editor.isActive('code'),
    }),
  });

  const [showLinkInput, setShowLinkInput] = useState(false);

  const [link, setLink] = useState('');

  const applyLink = useCallback(() => {
    if (!editor) {
      return;
    }

    const markRange = editor.chain().focus().extendMarkRange('link');

    link === '' ? markRange.unsetLink().run() : markRange.setLink({ href: link }).run();

    setShowLinkInput(false);

    setLink('');
  }, [editor, link]);

  const onClick = useCallback(() => {
    setLink(editor.getAttributes('link').href || '');
    setShowLinkInput(true);
  }, [editor]);

  return (
    <>
      <IconLabel
        iconId={'link'}
        iconSize={16}
        onClick={onClick}
        disabled={isActiveCode}
        className={classNames(classes['inline-tools__item'], {
          [classes['inline-tools__item--active']]: isActive,
        })}
      />

      <Modal
        contentStyle={{ width: '23rem' }}
        isOpen={showLinkInput}
        body={
          <div className={linkClasses['link-modal']}>
            <InputField
              value={link}
              onChange={({ target }) => setLink(target.value)}
              placeholder={'https://'}
            />
            <Button
              type={ButtonType.primary}
              label={t('common.apply')}
              onClick={applyLink}
              disabled={Boolean(link.length && !isUrl(link) && !link.startsWith('mailto:'))}
              fullWidth
            />
          </div>
        }
        onClose={() => setShowLinkInput(false)}
        root={'modal-editor-root'}
      />
    </>
  );
};
